import { StateFrom } from 'xstate';

import {
  AuthenticatedRoutes,
  CommonRoutes,
  OnboardingRoutes,
  Routes,
  UnauthenticatedRoutes,
} from '@/router/routes';
import { RootStates } from '@/router/state/root/component/root-states';
import { rootStateMachine } from '@/router/state/root/root-state-machine';

export type RootState = StateFrom<typeof rootStateMachine>;

function findMostNestedValue(stateValue: any): string {
  if (typeof stateValue === 'string') {
    return stateValue;
  }

  if (typeof stateValue === 'object' && stateValue !== null) {
    const keys = Object.keys(stateValue);
    if (keys.length > 0) {
      const firstKey = keys[0];
      return findMostNestedValue(stateValue[firstKey]);
    }
  }

  return '';
}

export function getRouteFromState(state: RootState, currentPathname?: string): Routes | undefined {
  if (state.matches({ [RootStates.Authenticated]: 'onboarding' })) {
    const onboardingSubState = state.children.onboardingMachine?.getSnapshot();

    if (onboardingSubState) {
      const onboardingStateValue = onboardingSubState.value;
      const mostNestedValue = findMostNestedValue(onboardingStateValue);

      const onboardingRoute = Object.values(OnboardingRoutes).find(
        routeKey => routeKey === mostNestedValue
      );

      if (onboardingRoute) {
        return onboardingRoute;
      }
    }
  }

  const stateValue = state.value;
  const mostNestedValue = findMostNestedValue(stateValue);

  // Find the route that matches the current state
  const targetRoute = [
    ...Object.values(AuthenticatedRoutes),
    ...Object.values(UnauthenticatedRoutes),
    ...Object.values(CommonRoutes),
    OnboardingRoutes.OnboardingCompletion, // Include completion route in main app routes
  ].find(routeKey => routeKey === mostNestedValue);

  // In unauthenticated state, let React Router handle navigation between public routes
  if (state.matches(RootStates.Unauthenticated)) {
    const isOnUnauthenticatedRoute = Object.values(UnauthenticatedRoutes).some(
      route => route === currentPathname
    );

    if (isOnUnauthenticatedRoute) {
      // Already on a valid public route, let React Router handle navigation
      return undefined;
    }

    // Not on a public route, redirect to the state machine's target
    return targetRoute;
  }

  // In authenticated main workspace, check if we need initial navigation
  if (state.matches({ [RootStates.Authenticated]: 'main' })) {
    // Check if current path matches any authenticated route pattern
    const authenticatedRoutePatterns = Object.values(AuthenticatedRoutes).map(route => {
      const pattern = route.replace(/:([^/]+)\?/g, '(?:/[^/]+)?').replace(/:[^/]+/g, '[^/]+');
      // Allow nested routes under this pattern (e.g., /settings/*)
      return `${pattern}(/.*)?`;
    });

    const isOnAuthenticatedRoute = authenticatedRoutePatterns.some(pattern =>
      new RegExp(`^${pattern}$`).test(currentPathname || '')
    );

    if (!isOnAuthenticatedRoute && targetRoute) {
      // Need to navigate from login/public route to authenticated workspace
      return targetRoute;
    }

    // Otherwise, let React Router handle all navigation
    return undefined;
  }

  return targetRoute;
}
